import {
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
} from "@chakra-ui/react";
import { FunctionComponent, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { unitsState } from "../state/state";
import { UnitTypes, Units } from "../types/sharedTypes";
import { unitFormatter } from "../utils/units";

type ObstacleFormValueInputProps = {
  label: string;
  value: number;
  onValueChange: (newValue: number) => void;
};

export const ObstacleFormValueInput: FunctionComponent<
  ObstacleFormValueInputProps
> = ({ label, value, onValueChange }) => {
  const { t } = useTranslation();
  const id = useId();
  const unit = useRecoilValue(unitsState);
  const step = 0.5;
  const min = 1;

  const [input, setInput] = useState<string>(
    t("formatting.numberMaxOneDecimalPoint", { number: value })
  );

  return (
    <FormControl alignItems="center">
      <Grid templateColumns="1fr 2fr">
        <GridItem>
          <FormLabel htmlFor={id} verticalAlign="middle">
            {label}
          </FormLabel>
        </GridItem>
        <GridItem>
          <NumberInput
            id={id}
            inputMode="numeric"
            min={min}
            step={step}
            value={input}
            keepWithinRange={true}
            onChange={(newValueAsString) => {
              setInput(newValueAsString);
              const valueAsNumber = parseFloat(
                newValueAsString.replaceAll(",", ".")
              );
              const shouldUpdate =
                newValueAsString.replaceAll(",", ".").replace(/\.0+$/, "") ===
                  valueAsNumber
                    .toString()
                    .replaceAll(",", ".")
                    .replace(/\.0+$/, "") &&
                valueAsNumber >= min &&
                valueAsNumber % step < 0.01;

              if (shouldUpdate) {
                onValueChange(valueAsNumber);
              }
            }}
            onBlur={(e) => {
              const value = parseFloat(e.target.value.replaceAll(",", "."));
              if (isNaN(value)) {
                // Input could not be parsed as number.
                // Reset input to value stored in state.
                setInput(value.toString());
              } else {
                // Input could be parsed as number.
                // Update store value.
                // Further validation is done in useEffects below.
                const revisedValue = Math.max(
                  min,
                  Math.round(value / step) * step
                );
                onValueChange(revisedValue);
                setInput(revisedValue.toString());
              }
            }}
            isValidCharacter={(value) => {
              return value.match(/^[0-9+\-.,]$/) ? true : false;
            }}
            format={(value) => {
              const formattedValue = t("formatting.numberMaxTwoDecimalPoints", {
                number: value,
              });
              return formattedValue === "NaN" ? value : formattedValue;
            }}
            clampValueOnBlur={false}
            onKeyUp={(e) => {
              // Handle keys 'Enter' and 'NumpadEnter'
              if (e.code.toLowerCase().includes("enter")) {
                (e.target as HTMLInputElement).blur();
              }
            }}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <Text my="0" ml="1">
            {input}m
            {unit === Units.Imperial &&
              " = " +
                unitFormatter(parseFloat(input), UnitTypes.Meter, unit, true) +
                "ft"}
          </Text>
        </GridItem>
      </Grid>
    </FormControl>
  );
};
